<template>
    <v-dialog
        v-model="dialog"
        persistent
        width="90%"
        max-width="90%"
        @keydown.esc="cancel"
        @click:outside="cancel"
    >
        <v-card>
            <v-card-title v-if="!adding">
                Elementos de contenido
                <v-text-field
                    outlined
                    flat
                    hide-details
                    clearable
                    prepend-inner-icon="mdi-magnify"
                    label="Buscar"
                    v-model="searchText"
                    class="ml-4 mr-4"
                    ref="searchTextField"
                />
                <v-btn
                    color="primary"
                    @click="addElement"
                    :disabled="!selectedCategory"
                >
                    <v-icon class="mr-2">mdi-plus</v-icon>Añadir rápido</v-btn
                >
            </v-card-title>

            <v-card-title v-if="adding">
                Creación rápida de un elemento de contenido
            </v-card-title>

            <v-card-text
                astyle="height: calc(80vh)"
                v-if="adding"
                class="pt-4 text-body-1"
            >
                <perfect-scrollbar>
                    <p>
                        Vas a crear un nuevo elemento de contenido de forma
                        rápida.
                    </p>
                    <p>
                        Únicamente te pediremos el nombre del mismo, que se
                        traducirá automáticamente al resto de idiomas
                        configurados.
                    </p>

                    <v-text-field
                        outlined
                        label="Nombre"
                        v-model="newName"
                        class="mt-8"
                        ref="newNameTextField"
                        @keydown.enter.stop="endAddition()"
                        @keydown.esc.stop="cancelAddition()"
                    />

                    <div>
                        <v-spacer />
                        <v-btn
                            color="green white--text"
                            @click="endAddition"
                            class="mr-4"
                            :disabled="!newName"
                            ><v-icon class="mr-2">mdi-content-save</v-icon
                            >Guardar</v-btn
                        >
                        <v-btn color="red white--text" @click="cancelAddition"
                            ><v-icon class="mr-2">mdi-close</v-icon
                            >Cancelar</v-btn
                        >
                    </div>
                </perfect-scrollbar>
            </v-card-text>

            <v-card-text style="height: calc(70vh)" v-if="!adding">
                <div class="d-flex" style="width: 100%; height: 100%">
                    <div class="pa-4" style="min-width: 300px; width: 300px">
                        <v-list>
                            <v-list-item-group
                                :value="selectedCategory"
                                color="primary"
                            >
                                <v-list-item
                                    link
                                    value=""
                                    @click="selectCategory('')"
                                >
                                    <v-list-item-content>
                                        Todos los elementos
                                    </v-list-item-content>
                                </v-list-item>
                                <v-list-item
                                    link
                                    value="HIGHLIGHT"
                                    @click="selectCategory('HIGHLIGHT')"
                                >
                                    <v-list-item-content>
                                        Highlights
                                    </v-list-item-content>
                                </v-list-item>
                                <v-list-item
                                    link
                                    value="INCLUSION"
                                    @click="selectCategory('INCLUSION')"
                                >
                                    <v-list-item-content>
                                        Inclusiones / Exclusiones
                                    </v-list-item-content>
                                </v-list-item>
                                <v-list-item
                                    link
                                    value="UNSUITNESS"
                                    @click="selectCategory('UNSUITNESS')"
                                >
                                    <v-list-item-content>
                                        No apto
                                    </v-list-item-content>
                                </v-list-item>
                                <v-list-item
                                    link
                                    value="ITINERARY"
                                    @click="selectCategory('ITINERARY')"
                                >
                                    <v-list-item-content>
                                        Puntos de itinerario
                                    </v-list-item-content>
                                </v-list-item>
                            </v-list-item-group>
                        </v-list>
                    </div>

                    <div
                        class="flex-grow-1 fill-height"
                        style="overflow: hidden"
                    >
                        <perfect-scrollbar>
                            <v-list>
                                <v-list-item-group v-model="selection" multiple>
                                    <v-list-item
                                        v-for="item in items"
                                        :key="item.id"
                                        :value="item.id"
                                    >
                                        <template v-slot:default="{ active }">
                                            <v-list-item-action v-if="multiple">
                                                <v-checkbox
                                                    :input-value="active"
                                                    color="primary"
                                                ></v-checkbox>
                                            </v-list-item-action>

                                            <v-list-item-content
                                                @click.stop="
                                                    selectSingleItem(item)
                                                "
                                            >
                                                <v-list-item-title>{{
                                                    item.name
                                                }}</v-list-item-title>
                                                <v-list-item-subtitle
                                                    class="pt-2 orange--text"
                                                    v-if="!item.reviewed"
                                                >
                                                    <v-icon small color="orange"
                                                        >mdi-alert</v-icon
                                                    >
                                                    Contenido no revisado
                                                </v-list-item-subtitle>
                                            </v-list-item-content>
                                        </template>
                                    </v-list-item>
                                </v-list-item-group>
                            </v-list>
                        </perfect-scrollbar>
                    </div>
                </div>
            </v-card-text>

            <v-card-actions v-if="multiple && !adding">
                <v-spacer />
                <v-btn
                    color="green white--text"
                    @click="acceptSelection"
                    :disabled="!selection.length"
                    ><v-icon class="mr-2">mdi-content-save</v-icon>Añadir
                    seleccionados</v-btn
                >
                <v-btn color="red white--text" @click="cancel"
                    ><v-icon class="mr-2">mdi-close</v-icon>Cancelar</v-btn
                >
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<style scoped lang="scss">
.ps {
    height: 100%;
}

.dropbox {
    height: 100px;
    width: 100%;
}
</style>

<script lang="ts">
import { EventBus } from "@/components/EventBus";
import { GetAllContentElementsResponse } from "@/services/ClientInterfaces";
import {
    ContentSelectorRequest,
    ContentSelectorResponse,
    DataClient,
} from "@/services/DataClient";
import { Utilities } from "@/services/Utilities";
import { Component, Prop, Vue } from "vue-property-decorator";
import { PerfectScrollbar } from "vue2-perfect-scrollbar";
import {
    VDialog,
    VCard,
    VBtn,
    VList,
    VListItemGroup,
    VListItem,
    VListItemContent,
    VTextField,
    VListItemAction,
    VCheckbox,
    VListItemTitle,
    VListItemSubtitle,
} from "vuetify/lib";

@Component
export default class extends Vue {
    dialog = false;
    data: any = {};

    rawItems: GetAllContentElementsResponse = [];

    multiple = false;
    selectedCategory = "";
    searchText = "";
    selection: string[] = [];

    adding = false;
    newName = "";
    newDataAdded = false;

    get items(): GetAllContentElementsResponse {
        const values = Utilities.sortAndFilter(
            this.selectedCategory
                ? this.rawItems.filter((x) => x.type === this.selectedCategory)
                : this.rawItems,
            "name",
            this.searchText,
            (item, searchText) =>
                Utilities.objectIncludes(item, ["name"], searchText)
        );

        return values;
    }

    mounted() {
        EventBus.$on(
            DataClient.ContentElements.OpenSelectorEvent,
            this.handleOpenEvent
        );
    }

    beforeDestroy() {
        EventBus.$off(
            DataClient.ContentElements.OpenSelectorEvent,
            this.handleOpenEvent
        );
    }

    reset() {
        this.dialog = false;

        this.rawItems = [];
        this.newDataAdded = false;
        this.selection = [];
        this.selectedCategory = "";
    }

    selectCategory(category: string) {
        this.selectedCategory = category;
        this.focus("searchTextField");
    }

    async refresh() {
        this.rawItems = await DataClient.ContentElements.getAll();
    }

    async handleOpenEvent(event: ContentSelectorRequest) {
        this.multiple = event.multiple === true;
        this.selectedCategory = event.type || "";
        this.searchText = "";

        await this.refresh();

        this.dialog = true;

        this.focus("searchTextField");
    }

    accept(data: ContentSelectorResponse) {
        DataClient.ContentElements.closeSelector(data);
        this.reset();
    }

    cancel() {
        DataClient.ContentElements.closeSelector();
        this.reset();
    }

    addElement() {
        this.newName = "";
        this.adding = true;
        this.focus("newNameTextField");
    }

    async endAddition() {
        if (this.newName) {
            await DataClient.ContentElements.add({
                name: {
                    es: this.newName,
                },
                type: this.selectedCategory,
            });

            this.newDataAdded = true;
            await this.refresh();

            this.adding = false;
        }
    }

    cancelAddition() {
        this.adding = false;
        this.newName = "";
    }

    selectSingleItem(item: GetAllContentElementsResponse[0]) {
        if (this.selection.length > 0) {
            // add item to selection if not already selected
            if (!this.selection.includes(item.id)) {
                this.selection.push(item.id);
            }
        } else {
            this.accept({
                newDataAdded: this.newDataAdded,
                selection: [item.id],
            });
        }
    }

    acceptSelection() {
        this.accept({
            newDataAdded: this.newDataAdded,
            selection: this.selection,
        });
    }

    focus(reference: string) {
        setTimeout(() => {
            const htmlInputElement = this.$refs[reference] as HTMLInputElement;
            if (htmlInputElement) {
                htmlInputElement.focus();
            }
        }, 100);
    }
}
</script>
